import { z } from 'zod';

export const TITLE_LIMIT = 512;
export const TLDR_LIMIT = 512;
export const URL_LIMIT = 2048;
export const STORAGE_KEY_LIMIT = 1024;
export const DIAGNOSTIC_LIMIT = 4000;

const clamp = (value: string, max: number): string =>
  value.length > max ? value.slice(0, max) : value;

export const requiredLimitedString = (label: string, max: number) =>
  z
    .string()
    .transform((value) => value.trim())
    .pipe(z.string().min(1, `${label} is required`))
    .transform((value) => clamp(value, max));

export const optionalLimitedString = (max: number) =>
  z
    .string()
    .optional()
    .nullable()
    .transform((value) => {
      if (typeof value !== 'string') return null;
      const trimmed = value.trim();
      if (!trimmed) return null;
      return clamp(trimmed, max);
    });

const isSafeHttpOrRelativeUrl = (value: string): boolean => {
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return true;
  }
  return parsed.protocol === 'http:' || parsed.protocol === 'https:';
};

export const optionalSafeUrl = (max: number) =>
  optionalLimitedString(max).refine((value) => value === null || isSafeHttpOrRelativeUrl(value), {
    message: 'URL must be an http(s) or relative URL',
  });

export const optionalCategoryKey = z
  .string()
  .min(1)
  .max(64)
  .optional()
  .nullable()
  .transform((value) => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  });

export const ISO_DATETIME = z.string().datetime({ offset: true });
export const UUID = z.string().uuid();
